import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentTextIcon,
  BoltIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeSolid,
  UsersIcon as UsersSolid,
  ChatBubbleBottomCenterTextIcon as ChatBubbleBottomCenterTextSolid,
  DocumentTextIcon as DocumentTextSolid,
  BoltIcon as BoltSolid,
  BeakerIcon as BeakerSolid,
} from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { useTier } from '../hooks/useTier';
import { useAIBar } from '../contexts/AIBarContext';
import { useNotificationsContext } from '../contexts/NotificationsContext';

interface NavigationItem {
  id: string;
  label: string;
  path?: string;
  action?: () => void;
  icon: React.ComponentType<any>;
  solidIcon: React.ComponentType<any>;
  color: string;
  isSpecial?: boolean;
  requiresPro?: boolean;
}

const BottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPro } = useTier();
  const { minimize } = useAIBar();
  const { pendingFriendRequests } = useNotificationsContext();

  const navigationItems: NavigationItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/dashboard',
      icon: HomeIcon,
      solidIcon: HomeSolid,
      color: 'var(--accent-green)'
    },
    {
      id: 'food',
      label: 'Food',
      path: '/food-log',
      icon: DocumentTextIcon,
      solidIcon: DocumentTextSolid,
      color: '#00E0FF'
    },
    {
      id: 'supplements',
      label: 'Supps',
      path: '/supplements',
      icon: BeakerIcon,
      solidIcon: BeakerSolid,
      color: '#CE93D8'
    },
    {
      id: 'exercise',
      label: 'Exercise',
      path: '/exercise-log',
      icon: BoltIcon,
      solidIcon: BoltSolid,
      color: '#00E676'
    },
    {
      id: 'friends',
      label: 'Friends',
      path: '/friends',
      icon: UsersIcon,
      solidIcon: UsersSolid,
      color: 'var(--accent-blue)'
    },
    {
      id: 'coach',
      label: 'Coach',
      path: '/coach',
      icon: ChatBubbleBottomCenterTextIcon,
      solidIcon: ChatBubbleBottomCenterTextSolid,
      color: '#00E0FF'
    }
  ];

  const isActive = (item: NavigationItem) => {
    if (item.path) {
      if (item.path === '/dashboard') {
        return location.pathname === '/dashboard' || location.pathname === '/';
      }
      return location.pathname.startsWith(item.path);
    }
    return false;
  };

  const handleNavigation = (item: NavigationItem) => {
    // Always minimize AI chat when navigating
    minimize();

    if (item.action) {
      item.action();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <>
    <style>{`
      @keyframes friendPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(1.3); }
      }
    `}</style>
    <nav
      className="bottom-navigation"
      data-tour="bottom-nav"
      style={{
        position: 'fixed',
        bottom: 0, // Anchored to actual bottom
        left: 0,
        right: 0,
        zIndex: 1100, // Above AI bar
        background: 'var(--bg-card)',
        borderTop: '1px solid var(--border-light)',
        boxShadow: 'var(--shadow-elevated)',
        backdropFilter: 'blur(20px)',
        width: '100%',
        height: 'auto',
        display: 'block',
        visibility: 'visible',
        opacity: 1
      }}
    >
      <div 
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px 2px 10px 2px',
          minHeight: '68px',
          gap: '2px'
        }}
      >
        {navigationItems.map((item) => {
          const active = isActive(item);
          const Icon = active ? item.solidIcon : item.icon;
          const canUse = !item.requiresPro || isPro;
          const hasFriendAlert = item.id === 'friends' && pendingFriendRequests > 0;
          
          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item)}
              aria-label={item.label}
              className={`bottom-nav-item ${active ? 'active' : ''}`}
              data-tour={item.id === 'food' ? 'food-log' : item.id === 'supplements' ? 'supplements' : item.id === 'health' ? 'bloodwork' : item.id === 'analysis' ? 'analysis' : undefined}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: item.isSpecial && canUse ? 
                  `linear-gradient(135deg, ${item.color}20, ${item.color}10)` : 
                  'none',
                border: item.isSpecial && canUse ? 
                  `2px solid ${item.color}40` : 
                  'none',
                cursor: 'pointer',
                padding: item.isSpecial ? '6px 4px' : '4px 2px',
                minHeight: item.isSpecial ? '56px' : '52px',
                minWidth: item.isSpecial ? '44px' : '40px',
                flex: '1',
                maxWidth: item.isSpecial ? '70px' : '60px',
                color: !canUse ? 'var(--text-disabled)' :
                       active ? item.color :
                       hasFriendAlert ? '#00E0FF' :
                       item.isSpecial ? item.color :
                       'var(--text-secondary)',
                transition: 'all var(--transition-fast)',
                borderRadius: item.isSpecial ? '12px' : '8px',
                position: 'relative',
                opacity: !canUse ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (canUse && !active) {
                  e.currentTarget.style.color = item.isSpecial ? item.color : 'var(--text-primary)';
                  e.currentTarget.style.background = item.isSpecial ? 
                    `linear-gradient(135deg, ${item.color}30, ${item.color}15)` : 
                    'var(--bg-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (canUse && !active) {
                  e.currentTarget.style.color = item.isSpecial ? item.color : 'var(--text-secondary)';
                  e.currentTarget.style.background = item.isSpecial && canUse ? 
                    `linear-gradient(135deg, ${item.color}20, ${item.color}10)` : 
                    'none';
                }
              }}
            >
              {/* Pro badge for special features */}
              {item.isSpecial && !canUse && (
                <div 
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    background: 'var(--accent-gold)',
                    color: 'white',
                    fontSize: '8px',
                    fontWeight: 'bold',
                    padding: '1px 3px',
                    borderRadius: '4px',
                    lineHeight: '1'
                  }}
                >
                  PRO
                </div>
              )}
              
              {/* Glow effect for special buttons when available */}
              {item.isSpecial && canUse && (
                <div 
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    background: `radial-gradient(circle, ${item.color}15 0%, transparent 70%)`,
                    borderRadius: '12px',
                    pointerEvents: 'none'
                  }}
                />
              )}
              
              <div 
                style={{
                  position: 'relative',
                  marginBottom: '4px',
                  zIndex: 1
                }}
              >
                <Icon
                  style={{
                    width: item.isSpecial ? '24px' : '20px',
                    height: item.isSpecial ? '24px' : '20px',
                    strokeWidth: active || item.isSpecial ? 2.5 : hasFriendAlert ? 2.5 : 2,
                    filter: hasFriendAlert ? 'drop-shadow(0 0 6px #00E0FF)' : undefined
                  }}
                />
                {active && !hasFriendAlert && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-2px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '4px',
                      height: '4px',
                      background: item.color,
                      borderRadius: '50%'
                    }}
                  />
                )}
                {hasFriendAlert && (
                  <div
                    className="friend-request-pulse"
                    style={{
                      position: 'absolute',
                      top: '-1px',
                      right: '-3px',
                      width: '7px',
                      height: '7px',
                      background: '#00E0FF',
                      borderRadius: '50%',
                      boxShadow: '0 0 6px #00E0FF',
                      animation: 'friendPulse 2s ease-in-out infinite'
                    }}
                  />
                )}
              </div>
              <span 
                style={{
                  fontSize: item.isSpecial ? '8px' : '9px',
                  fontWeight: active || item.isSpecial ? 600 : 400,
                  lineHeight: '1',
                  textAlign: 'center',
                  fontFamily: 'var(--font-family)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  zIndex: 1,
                  position: 'relative'
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
      
      {/* Safe area for iOS devices */}
      <div style={{ height: 'env(safe-area-inset-bottom)', background: 'var(--bg-card)' }} />
    </nav>
    </>
  );
};

export default BottomNavigation; 